// BUILD-046 task 8.4: the list-state model for My_Tasks, the Group_Shared_View, the Oversight_View and the Team Work list
// (design "List-state model"; Req 2.12, 15.4 - 15.10).
//
// One reducer per list, evaluated independently. Exactly one of four phases is rendered:
//   loading  : spinner text only. Entered on mount, on Retry and on a Caller change. Never items, never empty text, never error text.
//   error    : "Could not load <list>", the backend message, a Retry control for THIS list only. Never items, never empty text.
//   empty    : a 2xx answer with zero items. Never error text.
//   data     : a 2xx answer with items. Never error text, never empty text.
// A failure (non-2xx, no answer, no answer in 30 s, a 404 because the route is not served, or an answer that is not the documented shape)
// can therefore never look like an empty list, and a count that could not be read can never look like zero (CountState below).
//
// Every request carries a position (epoch, seq): epoch is the Caller epoch from api/workShared.ts, seq counts requests for the list. An
// answer is applied only if it is for the LATEST request of the latest epoch; anything else (an older retry, the previous Caller) is
// dropped, so the previous Caller's items can never appear after a Caller change (Req 2.7).
//
// Pure module: no React, no I/O, no imports at runtime. Erasable TypeScript only (it is run by `node --experimental-strip-types`).

export type ListName = 'myTasks' | 'shared' | 'oversight' | 'team';

export const LIST_NAMES: readonly ListName[] = Object.freeze(['myTasks', 'shared', 'oversight', 'team'] as ListName[]);

export const LIST_LABELS: Readonly<Record<ListName, string>> = Object.freeze({
  myTasks: 'My tasks',
  shared: 'Group / Shared tasks',
  oversight: 'Team Work (oversight)',
  team: 'Team Work',
});

export type ListPhase = 'loading' | 'error' | 'empty' | 'data';
export type ListErrorKind = 'http' | 'network' | 'timeout' | 'invalid-response';

export interface ListError {
  kind: ListErrorKind;
  /** HTTP status, 0 when there was no answer. */
  status: number;
  code: string | null;
  message: string;
}

export interface ListState<T> {
  phase: ListPhase;
  /** Position of the latest accepted request: the Caller epoch, then the request number within the list. */
  epoch: number;
  seq: number;
  /** Non-empty only in `data`. */
  items: readonly T[];
  /** Non-null only in `error`. */
  error: ListError | null;
}

export type ListEvent<T> =
  | { type: 'request'; epoch: number; seq: number }
  | { type: 'success'; epoch: number; seq: number; items: readonly T[] }
  | { type: 'failure'; epoch: number; seq: number; error: ListError };

export function initialListState<T>(): ListState<T> {
  return { phase: 'loading', epoch: 0, seq: 0, items: [], error: null };
}

/** True when (epoch, seq) comes strictly after the state's position. */
function isNewer(state: { epoch: number; seq: number }, epoch: number, seq: number): boolean {
  return epoch > state.epoch || (epoch === state.epoch && seq > state.seq);
}

function isPosition(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

const INVALID_ANSWER: ListError = Object.freeze({
  kind: 'invalid-response',
  status: 200,
  code: null,
  message: 'The server answered, but not in the expected form.',
});

export function listReducer<T>(state: ListState<T>, event: ListEvent<T>): ListState<T> {
  if (!isPosition(event.epoch) || !isPosition(event.seq)) return state;
  if (event.type === 'request') {
    // A request supersedes everything before it: items and errors are cleared, so nothing stale can show while loading.
    if (!isNewer(state, event.epoch, event.seq)) return state;
    return { phase: 'loading', epoch: event.epoch, seq: event.seq, items: [], error: null };
  }
  // An answer counts only for the request that is still the latest, and only once.
  if (state.phase !== 'loading' || event.epoch !== state.epoch || event.seq !== state.seq) return state;
  if (event.type === 'failure') {
    return { phase: 'error', epoch: state.epoch, seq: state.seq, items: [], error: normalizeError(event.error) };
  }
  // A success whose items are not a list is a failure, never an empty list.
  if (!Array.isArray(event.items)) {
    return { phase: 'error', epoch: state.epoch, seq: state.seq, items: [], error: INVALID_ANSWER };
  }
  if (event.items.length === 0) return { phase: 'empty', epoch: state.epoch, seq: state.seq, items: [], error: null };
  return { phase: 'data', epoch: state.epoch, seq: state.seq, items: event.items, error: null };
}

/** What the view renders. A state in `error` is `error` whatever else it holds; `empty` and `data` follow the items. */
export function renderKind<T>(state: ListState<T>): ListPhase {
  if (state.phase === 'error') return 'error';
  if (state.phase === 'loading') return 'loading';
  return state.items.length === 0 ? 'empty' : 'data';
}

// ───────────────────────── outcomes to errors ─────────────────────────

function normalizeError(error: ListError | null | undefined): ListError {
  if (error === null || typeof error !== 'object') return { kind: 'network', status: 0, code: null, message: 'Something went wrong.' };
  return {
    kind: error.kind === 'http' || error.kind === 'timeout' || error.kind === 'invalid-response' ? error.kind : 'network',
    status: typeof error.status === 'number' ? error.status : 0,
    code: typeof error.code === 'string' ? error.code : null,
    message: typeof error.message === 'string' && error.message !== '' ? error.message : 'Something went wrong.',
  };
}

/** True for an answer the client dropped because the Caller changed: a view ignores it (no state change). */
export function isStaleOutcome(error: unknown): boolean {
  return error !== null && typeof error === 'object' && (error as { kind?: unknown }).kind === 'stale';
}

/**
 * Any thrown value to a ListError. Reads fields structurally (WorkApiError has kind, status, code, message), so this module imports
 * nothing. An unknown thrown value is a failure, never an empty list.
 */
export function toListError(thrown: unknown): ListError {
  if (thrown !== null && typeof thrown === 'object') {
    const t = thrown as { kind?: unknown; status?: unknown; code?: unknown; message?: unknown };
    return normalizeError({
      kind: (typeof t.kind === 'string' ? t.kind : 'network') as ListErrorKind,
      status: typeof t.status === 'number' ? t.status : 0,
      code: typeof t.code === 'string' ? t.code : null,
      message: typeof t.message === 'string' ? t.message : '',
    });
  }
  return normalizeError(null);
}

// ───────────────────────── the four lists ─────────────────────────

export type ListsState<T> = Readonly<Record<ListName, ListState<T>>>;

export function initialLists<T>(): ListsState<T> {
  return { myTasks: initialListState<T>(), shared: initialListState<T>(), oversight: initialListState<T>(), team: initialListState<T>() };
}

/**
 * Applies an event to ONE list. The other three entries are returned as the same objects, so an outcome for one list can never change
 * another (Req 15.10). An event that does not change the list returns `lists` itself.
 */
export function listsReducer<T>(lists: ListsState<T>, action: { list: ListName; event: ListEvent<T> }): ListsState<T> {
  const current = lists[action.list];
  if (current === undefined) return lists;
  const next = listReducer(current, action.event);
  if (next === current) return lists;
  return { ...lists, [action.list]: next };
}

/**
 * Starts a request for one list and allocates its seq. Retry uses the same call: it sends the request for THAT list only (Req 15.6);
 * the caller loads exactly the list named here.
 */
export function requestList<T>(lists: ListsState<T>, list: ListName, epoch: number): { lists: ListsState<T>; seq: number; epoch: number } {
  const seq = lists[list].epoch === epoch ? lists[list].seq + 1 : 1;
  return { lists: listsReducer(lists, { list, event: { type: 'request', epoch, seq } }), seq, epoch };
}

/**
 * Replaces the item with this id by the server's item, in a list that holds data (task 8.19: Done merges the SERVER's item, never a guess). A
 * list that is loading, failed or empty, or that does not hold the id, is returned unchanged (as the same object), so a merge can never turn a
 * failed or loading list into data, and an item the list did not have is not added.
 */
export function mergeListItem<T>(state: ListState<T>, id: string, item: T): ListState<T> {
  if (state.phase !== 'data') return state;
  let found = false;
  const items = state.items.map((candidate) => {
    if ((candidate as { id?: unknown } | null)?.id !== id) return candidate;
    found = true;
    return item;
  });
  return found ? { ...state, items } : state;
}

/** A Caller change: every list is requested again under the new epoch, and everything the previous Caller had is cleared (Req 2.7). */
export function requestAllLists<T>(lists: ListsState<T>, epoch: number): { lists: ListsState<T>; seqs: Readonly<Record<ListName, number>> } {
  let next = lists;
  const seqs = {} as Record<ListName, number>;
  for (const name of LIST_NAMES) {
    const started = requestList(next, name, epoch);
    next = started.lists;
    seqs[name] = started.seq;
  }
  return { lists: next, seqs };
}

// ───────────────────────── what each phase shows ─────────────────────────

/** The text of each phase. Exactly one shape is returned, and no shape carries another phase's fields. */
export type ListView<T> =
  | { kind: 'loading'; text: string }
  | { kind: 'error'; heading: string; detail: string; status: number; code: string | null; canRetry: true }
  | { kind: 'empty'; text: string }
  | { kind: 'data'; items: readonly T[] };

export function describeList<T>(state: ListState<T>, label: string, emptyText: string): ListView<T> {
  const kind = renderKind(state);
  if (kind === 'loading') return { kind, text: `Loading ${label}…` };
  if (kind === 'error') {
    const error = state.error ?? normalizeError(null);
    return { kind, heading: `Could not load ${label}`, detail: error.message, status: error.status, code: error.code, canRetry: true };
  }
  if (kind === 'empty') return { kind, text: emptyText };
  return { kind, items: state.items };
}

// ───────────────────────── counts ─────────────────────────

// The Open_Task_Count and the Shared_Queue_Count follow the same discipline: a count that could not be read is "Unavailable", never 0
// (Req 2.12, 12 "never zero"). Loading shows a placeholder, not a number.

export type CountPhase = 'loading' | 'error' | 'ready';

export interface CountState {
  phase: CountPhase;
  epoch: number;
  seq: number;
  /** Non-null only in `ready`. */
  value: number | null;
  error: ListError | null;
}

export type CountEvent =
  | { type: 'request'; epoch: number; seq: number }
  | { type: 'success'; epoch: number; seq: number; value: number }
  | { type: 'failure'; epoch: number; seq: number; error: ListError };

export function initialCountState(): CountState {
  return { phase: 'loading', epoch: 0, seq: 0, value: null, error: null };
}

export function countReducer(state: CountState, event: CountEvent): CountState {
  if (!isPosition(event.epoch) || !isPosition(event.seq)) return state;
  if (event.type === 'request') {
    if (!isNewer(state, event.epoch, event.seq)) return state;
    return { phase: 'loading', epoch: event.epoch, seq: event.seq, value: null, error: null };
  }
  if (state.phase !== 'loading' || event.epoch !== state.epoch || event.seq !== state.seq) return state;
  if (event.type === 'failure') return { phase: 'error', epoch: state.epoch, seq: state.seq, value: null, error: normalizeError(event.error) };
  if (!isPosition(event.value)) return { phase: 'error', epoch: state.epoch, seq: state.seq, value: null, error: INVALID_ANSWER };
  return { phase: 'ready', epoch: state.epoch, seq: state.seq, value: event.value, error: null };
}

export type CountView =
  | { kind: 'loading'; text: string }
  | { kind: 'unavailable'; text: string; canRetry: true }
  | { kind: 'value'; value: number; text: string };

export function describeCount(state: CountState): CountView {
  if (state.phase === 'error') return { kind: 'unavailable', text: 'Unavailable', canRetry: true };
  if (state.phase === 'ready' && state.value !== null) return { kind: 'value', value: state.value, text: String(state.value) };
  return { kind: 'loading', text: '…' };
}
